import React from 'react';

// chakra imports
import {
	Box,
	Drawer,
	DrawerBody,
	DrawerCloseButton,
	DrawerContent,
	DrawerOverlay,
	Flex,
	Icon,
	useColorModeValue,
	useDisclosure,
	useToken,
} from '@chakra-ui/react';
import Content from 'components/sidebar/components/Content';

// Assets
import { IoMenuOutline } from 'react-icons/io5';
import { RType } from 'routes/types';
import { useMemo } from 'react';

/** Hex → rgba helper */
function hexToRgba(hex: string, alpha: number): string {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Brand-aware scrollbar styles.
 * 14px hit-area for easy grabbing, but only ~8px visible thumb
 * via the transparent-border + background-clip trick.
 * Tinted with the current brand color so it harmonizes with the
 * active-route indicator bar.
 */
function useSidebarScrollbarSx() {
	const [brand300, brand400, brand500] = useToken('colors', ['brand.300', 'brand.400', 'brand.500']);
	const isDark = useColorModeValue(false, true);

	return useMemo(() => {
		const thumb = isDark ? hexToRgba(brand300, 0.3) : hexToRgba(brand400, 0.25);
		const thumbHover = isDark ? hexToRgba(brand300, 0.5) : hexToRgba(brand400, 0.45);
		const thumbActive = isDark ? hexToRgba(brand300, 0.65) : hexToRgba(brand500, 0.55);
		const track = isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';

		return {
			overflowY: 'auto' as const,
			overflowX: 'hidden' as const,
			scrollbarGutter: 'stable' as const,
			'&::-webkit-scrollbar': {
				width: '14px',
			},
			'&::-webkit-scrollbar-track': {
				background: track,
				borderRadius: '10px',
				margin: '8px 0',
			},
			'&::-webkit-scrollbar-thumb': {
				borderRadius: '10px',
				background: thumb,
				border: '4px solid transparent',
				backgroundClip: 'padding-box',
				minHeight: '48px',
				transition: 'background 0.2s ease',
			},
			'&::-webkit-scrollbar-thumb:hover': {
				background: thumbHover,
				border: '3px solid transparent',
				backgroundClip: 'padding-box',
			},
			'&::-webkit-scrollbar-thumb:active': {
				background: thumbActive,
				border: '3px solid transparent',
				backgroundClip: 'padding-box',
			},
			scrollbarWidth: 'thin' as const,
			scrollbarColor: `${thumb} ${track}`,
		};
	}, [brand300, brand400, brand500, isDark]);
}

function Sidebar({ routes, enableFavorites, ...rest }: { routes: RType[]; enableFavorites?: boolean; [x: string]: any }) {

	let variantChange = '0.2s linear';
	let shadow = useColorModeValue('14px 17px 40px 4px rgba(112, 144, 176, 0.08)', 'unset');
	let sidebarBg = useColorModeValue('white', 'navy.800');
	let sidebarBorderColor = useColorModeValue('secondaryGray.100', 'whiteAlpha.50');
	let sidebarMargins = '0px';
	const scrollbarSx = useSidebarScrollbarSx();

	// SIDEBAR
	return (
		<Box display={{ sm: 'none', xl: 'block' }} position='fixed' minH='100%'>
			<Box
				bg={sidebarBg}
				transition={variantChange}
				w='300px'
				h='100vh'
				m={sidebarMargins}
				minH='100%'
				boxShadow='none'
				borderRight='1px solid'
				borderColor={sidebarBorderColor}
				sx={scrollbarSx}>
				<Content routes={routes} enableFavorites={enableFavorites} />
			</Box>
		</Box>
	);
}

// FUNCTIONS
export function SidebarResponsive({ routes, enableFavorites }: { routes: RType[]; enableFavorites?: boolean }) {
	let sidebarBackgroundColor = useColorModeValue('white', 'navy.800');
	let menuColor = useColorModeValue('gray.400', 'white');
	// // SIDEBAR
	const { isOpen, onOpen, onClose } = useDisclosure();
	const btnRef = React.useRef();
	const scrollbarSx = useSidebarScrollbarSx();

	return (
		<Flex display={{ sm: 'flex', xl: 'none' }} alignItems='center'>
			<Flex ref={btnRef} w='max-content' h='max-content' onClick={onOpen} data-tour='sidebar-toggle'>
				<Icon
					as={IoMenuOutline}
					color={menuColor}
					my='auto'
					w='20px'
					h='20px'
					me='10px'
					_hover={{ cursor: 'pointer' }}
				/>
			</Flex>
			<Drawer
				isOpen={isOpen}
				onClose={onClose}
				placement={document.documentElement.dir === 'rtl' ? 'right' : 'left'}
				finalFocusRef={btnRef}>
				<DrawerOverlay />
				<DrawerContent w='285px' maxW='285px' bg={sidebarBackgroundColor}>
					<DrawerCloseButton
						zIndex='3'
						onClick={onClose}
						_focus={{ boxShadow: 'none' }}
						_hover={{ boxShadow: 'none' }}
					/>
					<DrawerBody maxW='285px' px='0rem' pb='0' sx={scrollbarSx}>
						<Content routes={routes} enableFavorites={enableFavorites} />
					</DrawerBody>
				</DrawerContent>
			</Drawer>
		</Flex>
	);
}
// PROPS

export default Sidebar;
